import { UseFormReturn } from 'react-hook-form';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useUpdateWorkerAvailability } from './use-update-worker-availability';
import { isOverlappingSegment } from '../utils/is-overlapping-segment';
import { SegmentsForEdit, TimeSlotFormContext } from '../wizard/time-slot.types';

type Props = {
  workerId: string;
  formMethods: UseFormReturn<TimeSlotFormContext, any, undefined>;
  originalAvailability: SegmentsForEdit[];
  availabilityDate: DateTime;
  fromDate: DateTime;
  searchTerm: string | undefined;
  cancel: () => void;
};
export const useHandleAvailabilitySubmission = ({
  workerId,
  formMethods,
  originalAvailability,
  availabilityDate,
  fromDate,
  searchTerm,
  cancel,
}: Props) => {
  const { t } = useTranslation('all-platforms-common');
  const { updateWorkerAvailability } = useUpdateWorkerAvailability(
    workerId,
    fromDate,
    searchTerm,
    cancel
  );
  const { handleSubmit: formHandleSubmit, watch, setError } = formMethods;
  const availabilitySegments = watch('availability');
  const handleSubmit = formHandleSubmit((data: TimeSlotFormContext) => {
    const overlappingIndex = data.availability.findIndex((segment) =>
      isOverlappingSegment(segment, data.availability)
    );
    const internallyInvalidIndex = data.availability.findIndex(
      (segment) =>
        DateTime.fromFormat(segment.utcStartDateTime, 'T') >=
        DateTime.fromFormat(segment.utcEndDateTime, 'T')
    );

    if (internallyInvalidIndex !== -1) {
      setError(`availability.${internallyInvalidIndex}.utcStartDateTime`, {
        message: t('edit_availability.timeslot_invalid'),
      });
      setError(`availability.${internallyInvalidIndex}.utcEndDateTime`, {
        message: t('edit_availability.timeslot_invalid'),
      });
      return;
    }
    if (overlappingIndex !== -1) {
      setError(`availability.${overlappingIndex}.utcStartDateTime`, {
        message: t('edit_availability.timeslot_overlap'),
      });
      setError(`availability.${overlappingIndex}.utcEndDateTime`, {
        message: t('edit_availability.timeslot_overlap'),
      });
      return;
    }
    updateWorkerAvailability({
      updatedSegments: data.availability.map((segment) => ({
        segmentId: segment.segmentId,
        startDateTime: DateTime.fromFormat(segment.utcStartDateTime, 'T').toISO(),
        endDateTime: DateTime.fromFormat(segment.utcEndDateTime, 'T').toISO(),
        availabilityDate: availabilityDate.toISO(),
      })),
      removedSegmentIds: originalAvailability
        .filter((segment) => !data.availability.find((s) => s.segmentId === segment.segmentId))
        .map((segment) => segment.segmentId),
    });
  });

  const handleDeleteAll = formHandleSubmit((data: TimeSlotFormContext) => {
    updateWorkerAvailability({
      updatedSegments: [],
      removedSegmentIds: availabilitySegments.map((segment) => segment.segmentId),
    });
  });

  return {
    handleSubmit,
    handleDeleteAll,
  };
};
